<template>
  <div>
    <b-row>
      <b-col>
        <div ref="dayWrapper"
             class="day-wrapper"
             data-test="day-wrapper"
             :style="{ height: columnHeightRem }"
        >
          <div
            v-for="(threads, threadIndex) in dayContainer"
            :key="'thread' + threadIndex"
            data-test="day-container-item"
            class="day-container-item"
            :class="{ 'swiper-no-swiping': activateDragDrop }"
          >
            <template v-for="(cellItems, i) in threads">
              <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
              <div
                v-if="threads.length < 5 || i === 0 || (threads.length > 4 && i === 1) || !isWeekView || canShowAllCellItems"
                :id="'overlapping-' + i"
                :key="i"
                :class="{ 'with-plus-number': threads.length >= 5 && isWeekView && !canShowAllCellItems }"
                class="thread"
                data-test="thread"
                drag-area
                @dragover.prevent
                @drop="activateDragDrop && $emit('dropLesson', { threads })"
              >
                <template v-if="threads.length < 5 || i === 0 || !isWeekView || canShowAllCellItems">
                  <span
                    v-for="cellItem in cellItems"
                    :key="cellItem.id"
                    class="cell-item"
                    :class="{ 'is-draggable': activateDragDrop }"
                    :style="getCellItemStyles(cellItem.minutes_begins_at, cellItem.minutes_ends_at, timeFrame.week_minutes_begins_at)"
                  >
                    <slot name="time-grid-cell"
                          :cell-item="cellItem"
                          :parallel-items-count="threads.length"
                          :index="i"
                    >
                      <!-- PARENT SHOULD OVERRIDE THIS SLOT -->
                      <span data-test="cell-item" />
                    </slot>
                  </span>
                </template>

                <b-button
                  v-if="i === 1 && isWeekView && threads.length >= 5 && !canShowAllCellItems"
                  :id="`lesson-plus-btn-${threads[0][0].id}`"
                  variant="transparent"
                  class="plus-number cell-item p-0"
                  :class="activateDragDrop ? 'is-draggable btn' : 'bg-transparent border-0'"
                  :style="getCellItemStyles(cellItems[0].minutes_begins_at, cellItems[0].minutes_ends_at, timeFrame.week_minutes_begins_at)"
                  data-cy="plus-number-button"
                  data-test="plus-number-button"
                  :data-lessons-length="threads.length"
                  :aria-label="`${threads.length - 1} ${$t('timetable.timetable_row.more_lessons')}`"
                  @click="$emit('view-all-cell-items', threads)"
                >
                  <div class="btn cell w-100 h-100 wrapper">
                    <div class="cell-body w-100 h-100 card single-cell-item week week-multi week-endless">
                      <div class="subject">+{{ threads.length - 1 }}</div>
                      <small>{{ $t('timetable.timetable_row.more_lessons') }}</small>
                    </div>
                  </div>
                </b-button>
              </div>
            </template>
          </div>
        </div>
        <div :class="{ 'weekend-background': showWeekendBackground }" data-test="weekend-background-overlay" />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import timeGridRendering from 'src/mixins/timeGrid/timeGridRendering.js'

export default {
  name: 'TimeGridColumn',
  components: {},

  mixins: [timeGridRendering],

  props: {
    dayContainer: {
      type: Array,
      required: true,
    },

    timeFrame: {
      type: Object,
      required: true,
      validator: function (value) {
        return ['week_minutes_begins_at', 'week_minutes_ends_at'].every(
          (item) => value && Object.prototype.hasOwnProperty.call(value, item),
        )
      },
    },

    activateDragDrop: {
      type: Boolean,
      default: false,
    },

    showWeekendBackground: {
      type: Boolean,
      default: false,
    },

    canShowAllCellItems: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    columnHeight() {
      return (this.timeFrame.week_minutes_ends_at - this.timeFrame.week_minutes_begins_at) * this.defaultColumnScale + 20
    },

    columnHeightRem() {
      return `${(1 / 16) * parseInt(this.columnHeight)}rem`
    }
  },

  emits: ['dropLesson', 'view-all-cell-items', 'mounted'],

  async mounted() {
    this.$emit('mounted')
  },
}
</script>

<style scoped lang="scss">
.day-container-item {
  display: flex;
  flex-wrap: wrap;
}

.thread {
  flex: 1 1;
  align-items: center;
  position: relative;
}

.single-cell-item {
  box-shadow: none !important;
  overflow: visible;
}

.cell-item {
  text-align: center;
  width: 100%;
  position: absolute;
  display: flex;
  justify-content: space-around;
  align-items: center;
  cursor: pointer;
  z-index: 1;

  :deep(.cell-item-inner) {
    transition: 0.1s;
  }

  &:not(.is-draggable):hover {
    :deep(.cell-item-inner) {
      transform: scale(1.01);
      box-shadow: 0 4px 12px 0 rgba($color-black-rgb, 0.12) !important;
      z-index: 2;
    }

    :deep(.week .cell-item-inner) {
      transform: scale(1.08);
    }

    @include media($mobile) {
      :deep(.cell-item-inner) {
        transform: none;
        box-shadow: none;
        z-index: initial;
      }

      :deep(.week .cell-item-inner) {
        transform: none;
      }
    }
  }

  &:not(.is-draggable):active {
    :deep(.cell-item-inner) {
      transform: none;
    }

    :deep(.week .cell-item-inner) {
      transform: none;
    }
  }
}

.plus-number.cell-item .wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0;
  cursor: pointer;
}

.plus-number.cell-item .single-cell-item {
  background: none;
}

.plus-number.cell-item .subject {
  padding: 10px;
  background: $color-white;
  border-radius: 50%;
  height: 35px;
  width: 35px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  box-shadow: 0 4px 12px 0 rgba($color-black-rgb, 0.12) !important;
  font-weight: $font-weight-bolder;
  font-size: pxtorem(13);
}

.plus-number.cell-item small {
  font-size: pxtorem(9);
  color: $color-black;
}

.with-plus-number .cell-item {
  width: 50%;
}

.with-plus-number:nth-child(2) .cell-item {
  width: 150%;
  left: -50%;
}

.with-plus-number:nth-child(2) .cell-item .week-endless {
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  gap: 10px;
}

.with-plus-number:nth-child(2) .cell-item.is-draggable .week-endless::before {
  content: '';
  position: absolute;
  left: 0;
  width: 100%;
  background: $color-white;
  top: 0;
  bottom: 0;
  z-index: -1;
  border-radius: 10px;
}

.day-wrapper {
  position: relative;
  user-select: none;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    height: 100%;
    width: 1px;
    top: 0;
    z-index: -5;
    background: $color-secondary;
  }
}

.weekend-background {
  background-color: $color-bg-secondary;
  position: absolute;
  top: 0;
  z-index: -7;
  width: 100%;
  height: 100%;
}

.week-endless {
  justify-content: space-between;
  align-items: flex-start;
  flex-flow: row wrap;
  margin: 0;
  padding: 3px 5px;
  border-radius: 3px;
  box-shadow: none !important;
  overflow: visible;
  display: flex;
}

.week-endless::before {
  content: none;
}
</style>
